import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Radio,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconCheck,
  IconCode,
  IconFileTypeHtml,
  IconRefresh,
} from "@tabler/icons-react";
import { httpsCallable } from "firebase/functions";
import { doc, runTransaction, Timestamp } from "firebase/firestore";
import { useFirestore, useFunctions } from "../../../helpers/firebaseContext";
import {
  ComponentSection,
  ContractLang,
  FieldTypes,
  GenerateKYCProductPreviewHTMLResult,
  GenerateKYCProductPreviewPDFProps,
  ItemReference,
  KYCPage,
  KYCProduct,
  PageOverride,
  SectionField,
} from "../../../types";
import { TextFieldEditor } from "./TextFieldEditorModal";

type PdfEditorTargetType = "section" | "field";

type PdfEditorTargetMeta = {
  targetType: PdfEditorTargetType;
  pageId: string;
  pageTitle: string;
  pageTitleAr: string;
  componentIndex: number;
  sectionId: string;
  sectionTitle: string;
  sectionTitleAr: string;
  fieldIndex?: number;
  fieldKey?: string;
  fieldType?: string;
  fieldLabel?: string;
  fieldLabelAr?: string;
};

type SectionDraft = Pick<
  ComponentSection,
  "section_title" | "section_title_ar" | "section_description" | "section_description_ar"
>;

type FieldDraft = Pick<
  SectionField,
  "label" | "label_ar" | "placeholder" | "placeholder_ar" | "value" | "value_ar"
>;

type ProductItemReference = ItemReference | string;

type RootEditAnchor = {
  rootIndex: number;
  kind: "direct" | "nested";
  basePageId: string;
};

type SelectedSource = {
  page: KYCPage;
  section?: ComponentSection;
  field?: SectionField;
};

type SaveMode = "override" | "shared";

type KYCProductPdfTextEditorProps = {
  product: KYCProduct;
  products: KYCProduct[];
  pages: KYCPage[];
  onSaved?: () => Promise<void> | void;
};

const FIRESTORE_PATHS = {
  CONFIG: "config",
  KYC_CONFIG: "kyc_configuration",
  KYC_PRODUCTS: "kyc_products",
  KYC_PAGES: "kyc_pages",
} as const;

const LIST_STYLES = {
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
} as const;

const PRODUCT_PAGE_ID_MAX_LENGTH = 115;
const TEMPLATE_SYNTAX_PATTERN = /\{\{\s*(#if|else|\/if|[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*)\s*\}\}/;

const getProductTitle = (product: KYCProduct): string =>
  product.title?.trim() || product.id;

const getReferenceId = (reference: ProductItemReference): string =>
  typeof reference === "string" ? reference : reference.id;

const getReferenceType = (reference: ProductItemReference): ItemReference["type"] =>
  typeof reference === "string" ? "page" : reference.type;

const getProductReferences = (product: KYCProduct): ProductItemReference[] =>
  (Array.isArray(product.pages) ? product.pages : []) as ProductItemReference[];

const toTextValue = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.join(", ");
  return typeof value === "string" ? value : String(value);
};

const upsertPageOverride = (
  overrides: PageOverride[] | undefined,
  pageId: string,
  overridePageId: string,
): PageOverride[] => {
  const existing = overrides || [];
  const matchIndex = existing.findIndex((override) => override.page_id === pageId);

  if (matchIndex === -1) {
    return [...existing, { page_id: pageId, override_page_id: overridePageId }];
  }

  return existing.map((override, index) =>
    index === matchIndex
      ? { page_id: pageId, override_page_id: overridePageId }
      : override,
  );
};

const mergeReferenceOverrides = (
  inheritedOverrides: Map<string, string>,
  reference: ProductItemReference,
): Map<string, string> => {
  const nextOverrides = new Map(inheritedOverrides);

  if (typeof reference !== "string") {
    reference.override_pages?.forEach((override) => {
      nextOverrides.set(override.page_id, override.override_page_id);
    });
  }

  return nextOverrides;
};

const findResolvedPageBaseId = (
  product: KYCProduct,
  targetPageId: string,
  productsById: Map<string, KYCProduct>,
  inheritedOverrides: Map<string, string> = new Map(),
  visitedProductIds: Set<string> = new Set(),
): string | null => {
  if (visitedProductIds.has(product.id)) return null;

  const nextVisited = new Set(visitedProductIds);
  nextVisited.add(product.id);

  for (const reference of getProductReferences(product)) {
    const referenceId = getReferenceId(reference);
    const referenceType = getReferenceType(reference);
    const referenceOverrides = mergeReferenceOverrides(inheritedOverrides, reference);

    if (referenceType === "page") {
      const resolvedPageId = referenceOverrides.get(referenceId) || referenceId;
      if (referenceId === targetPageId || resolvedPageId === targetPageId) {
        return referenceId;
      }
      continue;
    }

    const nestedProduct = productsById.get(referenceId);
    if (!nestedProduct) continue;

    const nestedMatch = findResolvedPageBaseId(
      nestedProduct,
      targetPageId,
      productsById,
      referenceOverrides,
      nextVisited,
    );

    if (nestedMatch) return nestedMatch;
  }

  return null;
};

const findRootEditAnchor = (
  product: KYCProduct,
  productsById: Map<string, KYCProduct>,
  targetPageId: string,
): RootEditAnchor | null => {
  const references = getProductReferences(product);

  for (let index = 0; index < references.length; index += 1) {
    const reference = references[index];
    const referenceId = getReferenceId(reference);
    const referenceType = getReferenceType(reference);

    if (referenceType === "page") {
      if (referenceId === targetPageId) {
        return { rootIndex: index, kind: "direct", basePageId: referenceId };
      }
      continue;
    }

    const nestedProduct = productsById.get(referenceId);
    if (!nestedProduct) continue;

    const basePageId = findResolvedPageBaseId(
      nestedProduct,
      targetPageId,
      productsById,
      mergeReferenceOverrides(new Map(), reference),
      new Set([product.id]),
    );

    if (basePageId) {
      return { rootIndex: index, kind: "nested", basePageId };
    }
  }

  return null;
};

const getProductsUsingPage = (
  products: KYCProduct[],
  productsById: Map<string, KYCProduct>,
  pageId: string,
): KYCProduct[] =>
  products.filter((product) => findResolvedPageBaseId(product, pageId, productsById));

const createSuggestedProductSpecificPageId = (productId: string, pageId: string): string => {
  return `${pageId}_${productId}`
    .replace(/[^A-Za-z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .slice(0, PRODUCT_PAGE_ID_MAX_LENGTH);
};

const getSelectedSource = (
  target: PdfEditorTargetMeta | null,
  pages: KYCPage[],
): SelectedSource | null => {
  if (!target) return null;

  const page = pages.find((item) => item.id === target.pageId);
  if (!page) return null;

  const section = page.components?.[target.componentIndex];
  const field =
    target.targetType === "field" && target.fieldIndex !== undefined
      ? section?.section_fields?.[target.fieldIndex]
      : undefined;

  return { page, section, field };
};

const createSectionDraft = (section?: ComponentSection): SectionDraft => ({
  section_title: section?.section_title || "",
  section_title_ar: section?.section_title_ar || "",
  section_description: section?.section_description || "",
  section_description_ar: section?.section_description_ar || "",
});

const createFieldDraft = (field?: SectionField): FieldDraft => ({
  label: field?.label || "",
  label_ar: field?.label_ar || "",
  placeholder: field?.placeholder || "",
  placeholder_ar: field?.placeholder_ar || "",
  value: toTextValue(field?.value),
  value_ar: toTextValue(field?.value_ar),
});

const isRichTextField = (field?: SectionField): boolean =>
  field?.type === FieldTypes.TEXT ||
  field?.type === FieldTypes.AGREEMENT_CHECKBOX ||
  field?.type === FieldTypes.THANK_YOU_TICK_MARK;

const isValueEditableField = (field?: SectionField): boolean =>
  field?.type === FieldTypes.INPUT ||
  field?.type === FieldTypes.DATE_PICKER ||
  field?.type === FieldTypes.COUNTRY_PICKER;

const patchPage = (
  page: KYCPage,
  target: PdfEditorTargetMeta,
  sectionDraft: SectionDraft | null,
  fieldDraft: FieldDraft | null,
): KYCPage => {
  const components = (page.components || []).map((section, sectionIndex) => {
    if (sectionIndex !== target.componentIndex) return section;

    if (target.targetType === "section") {
      return {
        ...section,
        ...(sectionDraft || createSectionDraft(section)),
      };
    }

    return {
      ...section,
      section_fields: (section.section_fields || []).map((field, fieldIndex) =>
        fieldIndex === target.fieldIndex
          ? {
              ...field,
              ...(fieldDraft || createFieldDraft(field)),
            }
          : field,
      ),
    };
  });

  return {
    ...page,
    components,
    eTag: Timestamp.now().toMillis().toString(),
  };
};

const applyProductSpecificOverride = (
  product: KYCProduct,
  anchor: RootEditAnchor,
  selectedPageId: string,
  newPageId: string,
): KYCProduct => {
  const references = getProductReferences(product);
  const updatedReferences = references.map((reference, index) => {
    if (index !== anchor.rootIndex) return reference;

    if (anchor.kind === "direct") {
      return typeof reference === "string"
        ? newPageId
        : { ...reference, id: newPageId };
    }

    if (typeof reference === "string") return reference;

    return {
      ...reference,
      override_pages: upsertPageOverride(
        reference.override_pages,
        anchor.basePageId,
        newPageId,
      ),
    };
  });

  return {
    ...product,
    pages: updatedReferences as ItemReference[],
    pdf_page_index:
      anchor.kind === "direct"
        ? (product.pdf_page_index || []).map((pageId) =>
            pageId === anchor.basePageId || pageId === selectedPageId ? newPageId : pageId,
          )
        : product.pdf_page_index,
  };
};

export const KYCProductPdfTextEditor: React.FC<KYCProductPdfTextEditorProps> = ({
  product,
  products,
  pages,
  onSaved,
}) => {
  const db = useFirestore();
  const functions = useFunctions();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [editorProduct, setEditorProduct] = useState(product);
  const [editorPages, setEditorPages] = useState(pages);
  const [html, setHtml] = useState("");
  const [language, setLanguage] = useState<ContractLang>("ar");
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<PdfEditorTargetMeta | null>(null);
  const [sectionDraft, setSectionDraft] = useState<SectionDraft | null>(null);
  const [fieldDraft, setFieldDraft] = useState<FieldDraft | null>(null);
  const [saveMode, setSaveMode] = useState<SaveMode>("override");
  const [overridePageId, setOverridePageId] = useState("");

  useEffect(() => setEditorProduct(product), [product]);
  useEffect(() => setEditorPages(pages), [pages]);

  const editorProducts = useMemo(
    () =>
      products.some((item) => item.id === editorProduct.id)
        ? products.map((item) => (item.id === editorProduct.id ? editorProduct : item))
        : [...products, editorProduct],
    [editorProduct, products],
  );

  const productsById = useMemo(
    () => new Map(editorProducts.map((item) => [item.id, item])),
    [editorProducts],
  );

  const selectedSource = useMemo(
    () => getSelectedSource(selectedTarget, editorPages),
    [editorPages, selectedTarget],
  );

  const rootAnchor = useMemo(
    () =>
      selectedTarget
        ? findRootEditAnchor(editorProduct, productsById, selectedTarget.pageId)
        : null,
    [editorProduct, productsById, selectedTarget],
  );

  const productsUsingPage = useMemo(
    () =>
      selectedTarget
        ? getProductsUsingPage(editorProducts, productsById, selectedTarget.pageId)
        : [],
    [editorProducts, productsById, selectedTarget],
  );
  const pageUsageCount = productsUsingPage.length;
  const trimmedOverridePageId = overridePageId.trim();
  const overridePageIdError = useMemo(() => {
    if (saveMode !== "override") return null;
    if (!trimmedOverridePageId) return "Enter a page ID for the product-specific copy.";
    if (trimmedOverridePageId.includes("/")) return "Page ID cannot contain a slash.";
    if (trimmedOverridePageId === selectedTarget?.pageId) {
      return "Use a new page ID, not the currently selected page ID.";
    }
    if (editorPages.some((page) => page.id === trimmedOverridePageId)) {
      return `Page ID "${trimmedOverridePageId}" already exists.`;
    }
    return null;
  }, [editorPages, saveMode, selectedTarget?.pageId, trimmedOverridePageId]);

  const loadHtml = useCallback(async () => {
    setIsLoadingHtml(true);
    setSelectedTarget(null);

    try {
      const generatePreviewHTML = httpsCallable<
        GenerateKYCProductPreviewPDFProps,
        GenerateKYCProductPreviewHTMLResult
      >(functions, "generateKYCProductPreviewHTML");
      const result = await generatePreviewHTML({
        productId: editorProduct.id,
        baseUrl: window.location.origin,
        lang: language,
        showQRCodePDF: false,
      });

      setHtml(result.data.html);
    } catch (error) {
      console.error("Error generating product preview HTML:", error);
      showNotification({
        color: "red",
        title: "Preview Failed",
        message: "Failed to generate editable PDF preview.",
        autoClose: 5000,
      });
    } finally {
      setIsLoadingHtml(false);
    }
  }, [editorProduct.id, functions, language]);

  useEffect(() => {
    void loadHtml();
  }, [loadHtml]);

  useEffect(() => {
    if (!selectedTarget || !selectedSource) {
      setSectionDraft(null);
      setFieldDraft(null);
      setOverridePageId("");
      return;
    }

    setSectionDraft(createSectionDraft(selectedSource.section));
    setFieldDraft(createFieldDraft(selectedSource.field));
    setSaveMode(pageUsageCount > 1 || rootAnchor?.kind === "nested" ? "override" : "shared");
    setOverridePageId(
      createSuggestedProductSpecificPageId(editorProduct.id, selectedTarget.pageId),
    );
  }, [editorProduct.id, pageUsageCount, rootAnchor?.kind, selectedSource, selectedTarget]);

  const handleIframeLoad = useCallback(() => {
    const iframeDocument = iframeRef.current?.contentDocument;
    if (!iframeDocument || iframeDocument.body.dataset.kycEditorBound === "true") {
      return;
    }

    iframeDocument.body.dataset.kycEditorBound = "true";
    const style = iframeDocument.createElement("style");
    style.textContent = `
      [data-kyc-editor-target="true"] {
        cursor: pointer !important;
        outline: 1px dashed rgba(34, 139, 230, 0.55);
        outline-offset: 2px;
        transition: background-color 120ms ease, outline-color 120ms ease, box-shadow 120ms ease;
      }
      [data-kyc-editor-kind="section"] {
        outline-color: rgba(134, 142, 150, 0.5);
      }
      [data-kyc-editor-target="true"]:hover {
        background: rgba(34, 139, 230, 0.08) !important;
        outline: 2px solid rgba(34, 139, 230, 0.9) !important;
      }
      [data-kyc-editor-selected="true"] {
        background: rgba(64, 192, 87, 0.1) !important;
        outline: 3px solid rgba(64, 192, 87, 0.95) !important;
        box-shadow: 0 0 0 4px rgba(64, 192, 87, 0.18) !important;
      }
    `;
    iframeDocument.head.appendChild(style);

    iframeDocument.addEventListener(
      "click",
      (event) => {
        const element = (event.target as Element | null)?.closest(
          "[data-kyc-editor-target='true']",
        ) as HTMLElement | null;

        if (!element) return;

        event.preventDefault();
        event.stopPropagation();

        const rawMeta = element.getAttribute("data-kyc-editor");
        if (!rawMeta) return;

        try {
          const meta = JSON.parse(rawMeta) as PdfEditorTargetMeta;
          iframeDocument
            .querySelectorAll("[data-kyc-editor-selected='true']")
            .forEach((item) => item.removeAttribute("data-kyc-editor-selected"));
          element.setAttribute("data-kyc-editor-selected", "true");
          setSelectedTarget(meta);
        } catch (error) {
          console.error("Unable to parse KYC PDF editor target metadata:", error);
        }
      },
      true,
    );
  }, []);

  const handleSaveShared = useCallback(async () => {
    if (!selectedTarget || !selectedSource) return;

    const pageRef = doc(
      db,
      FIRESTORE_PATHS.CONFIG,
      FIRESTORE_PATHS.KYC_CONFIG,
      FIRESTORE_PATHS.KYC_PAGES,
      selectedTarget.pageId,
    );
    let updatedPage: KYCPage | null = null;

    await runTransaction(db, async (transaction) => {
      const pageSnapshot = await transaction.get(pageRef);
      if (!pageSnapshot.exists()) {
        throw new Error(`Page "${selectedTarget.pageId}" no longer exists.`);
      }

      const currentPage = {
        id: pageSnapshot.id,
        ...pageSnapshot.data(),
      } as KYCPage;
      updatedPage = patchPage(currentPage, selectedTarget, sectionDraft, fieldDraft);
      transaction.set(pageRef, updatedPage);
    });

    if (updatedPage) {
      setEditorPages((currentPages) =>
        currentPages.map((page) =>
          page.id === updatedPage!.id ? updatedPage! : page,
        ),
      );
    }
  }, [db, fieldDraft, sectionDraft, selectedSource, selectedTarget]);

  const handleSaveProductOverride = useCallback(async () => {
    if (!selectedTarget || !selectedSource || !rootAnchor) return;
    if (overridePageIdError) {
      throw new Error(overridePageIdError);
    }

    const newPageId = trimmedOverridePageId;
    const pageRef = doc(
      db,
      FIRESTORE_PATHS.CONFIG,
      FIRESTORE_PATHS.KYC_CONFIG,
      FIRESTORE_PATHS.KYC_PAGES,
      selectedTarget.pageId,
    );
    const newPageRef = doc(
      db,
      FIRESTORE_PATHS.CONFIG,
      FIRESTORE_PATHS.KYC_CONFIG,
      FIRESTORE_PATHS.KYC_PAGES,
      newPageId,
    );
    const productRef = doc(
      db,
      FIRESTORE_PATHS.CONFIG,
      FIRESTORE_PATHS.KYC_CONFIG,
      FIRESTORE_PATHS.KYC_PRODUCTS,
      editorProduct.id,
    );

    let updatedPage: KYCPage | null = null;
    let updatedProduct: KYCProduct | null = null;

    await runTransaction(db, async (transaction) => {
      const pageSnapshot = await transaction.get(pageRef);
      const productSnapshot = await transaction.get(productRef);
      const newPageSnapshot = await transaction.get(newPageRef);

      if (!pageSnapshot.exists()) {
        throw new Error(`Page "${selectedTarget.pageId}" no longer exists.`);
      }

      if (!productSnapshot.exists()) {
        throw new Error(`Product "${editorProduct.id}" no longer exists.`);
      }

      if (newPageSnapshot.exists()) {
        throw new Error(`Generated page "${newPageId}" already exists.`);
      }

      const currentPage = {
        id: pageSnapshot.id,
        ...pageSnapshot.data(),
      } as KYCPage;
      const currentProduct = {
        id: productSnapshot.id,
        ...productSnapshot.data(),
      } as KYCProduct;

      const pageCopy: KYCPage = {
        ...currentPage,
        id: newPageId,
        title: `${currentPage.title || currentPage.id} (${getProductTitle(currentProduct)})`,
      };

      updatedPage = patchPage(pageCopy, selectedTarget, sectionDraft, fieldDraft);
      updatedProduct = applyProductSpecificOverride(
        currentProduct,
        rootAnchor,
        selectedTarget.pageId,
        newPageId,
      );

      transaction.set(newPageRef, updatedPage);
      transaction.set(productRef, updatedProduct, { merge: true });
    });

    if (updatedPage) {
      setEditorPages((currentPages) => [...currentPages, updatedPage!]);
    }

    if (updatedProduct) {
      setEditorProduct(updatedProduct);
    }
  }, [
    db,
    editorProduct.id,
    fieldDraft,
    overridePageIdError,
    rootAnchor,
    sectionDraft,
    selectedSource,
    selectedTarget,
    trimmedOverridePageId,
  ]);

  const handleSave = useCallback(async () => {
    if (!selectedTarget || !selectedSource) return;

    setIsSaving(true);
    try {
      if (saveMode === "override") {
        if (!rootAnchor) {
          throw new Error("Could not find where this page is attached to the product.");
        }
        await handleSaveProductOverride();
      } else {
        await handleSaveShared();
      }

      showNotification({
        color: "teal",
        title: "PDF Text Updated",
        message:
          saveMode === "override"
            ? "Saved as a product-specific page override."
            : "Saved on the shared page.",
        autoClose: 4000,
      });

      await onSaved?.();
      await loadHtml();
    } catch (error) {
      console.error("Error saving PDF text edit:", error);
      showNotification({
        color: "red",
        title: "Save Failed",
        message: error instanceof Error ? error.message : "Failed to save PDF text edit.",
        autoClose: 6000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    handleSaveProductOverride,
    handleSaveShared,
    loadHtml,
    onSaved,
    rootAnchor,
    saveMode,
    selectedSource,
    selectedTarget,
  ]);

  const field = selectedSource?.field;
  const richTextField = isRichTextField(field);
  const valueEditableField = isValueEditableField(field);
  const selectedDraftHasTemplateSyntax = [
    sectionDraft?.section_title,
    sectionDraft?.section_title_ar,
    sectionDraft?.section_description,
    sectionDraft?.section_description_ar,
    fieldDraft?.label,
    fieldDraft?.label_ar,
    fieldDraft?.placeholder,
    fieldDraft?.placeholder_ar,
    fieldDraft?.value,
    fieldDraft?.value_ar,
  ].some((value) => TEMPLATE_SYNTAX_PATTERN.test(value || ""));

  return (
    <Stack gap="md">
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" align="flex-start" gap="md">
          <div>
            <Group gap="xs">
              <Text fw={800}>{getProductTitle(editorProduct)}</Text>
              <Badge variant="light" color="gray" style={LIST_STYLES.mono}>
                {editorProduct.id}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Click a highlighted PDF block to edit the underlying page config.
            </Text>
          </div>
          <Group gap="xs">
            <SegmentedControl
              size="xs"
              value={language}
              onChange={(value) => setLanguage(value as ContractLang)}
              data={[
                { label: "AR", value: "ar" },
                { label: "EN", value: "en" },
              ]}
            />
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={15} />}
              onClick={() => void loadHtml()}
              loading={isLoadingHtml}
            >
              Refresh
            </Button>
          </Group>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Paper
          withBorder
          radius="md"
          style={{ height: "min(78vh, 900px)", overflow: "hidden" }}
        >
          {isLoadingHtml ? (
            <Group justify="center" h="100%">
              <Stack align="center" gap="sm">
                <Loader />
                <Text size="sm" c="dimmed">
                  Generating editable PDF HTML...
                </Text>
              </Stack>
            </Group>
          ) : (
            <iframe
              ref={iframeRef}
              title="Editable KYC PDF HTML preview"
              srcDoc={html}
              sandbox="allow-same-origin"
              onLoad={handleIframeLoad}
              style={{
                width: "100%",
                height: "100%",
                border: 0,
                background: "#f8f9fa",
              }}
            />
          )}
        </Paper>

        <Paper withBorder radius="md" p="md" style={{ height: "min(78vh, 900px)" }}>
          <ScrollArea h="100%" offsetScrollbars>
            {!selectedTarget ? (
              <Stack gap="md">
                <Group gap="xs">
                  <IconFileTypeHtml size={20} />
                  <Text fw={700}>No block selected</Text>
                </Group>
                <Text size="sm" c="dimmed">
                  Select a highlighted field or section in the PDF preview. The editor will show
                  the exact page, section, and field being changed before anything is saved.
                </Text>
              </Stack>
            ) : !selectedSource ? (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                This preview block points to a page or field that is not loaded in the editor.
                Refresh the KYC configuration and try again.
              </Alert>
            ) : (
              <Stack gap="md">
                <div>
                  <Group gap="xs" mb={4}>
                    <Badge color={selectedTarget.targetType === "field" ? "blue" : "gray"}>
                      {selectedTarget.targetType}
                    </Badge>
                    <Badge variant="light" color={rootAnchor?.kind === "nested" ? "violet" : "teal"}>
                      {rootAnchor?.kind === "nested" ? "Nested product" : "Direct page"}
                    </Badge>
                  </Group>
                  <Text fw={800}>{selectedTarget.pageTitle}</Text>
                  <Text size="xs" c="dimmed" style={LIST_STYLES.mono}>
                    page: {selectedTarget.pageId}
                  </Text>
                  <Text size="xs" c="dimmed" style={LIST_STYLES.mono}>
                    section: {selectedTarget.sectionId || "(no section id)"}
                  </Text>
                  {selectedTarget.targetType === "field" && (
                    <Text size="xs" c="dimmed" style={LIST_STYLES.mono}>
                      field: {selectedTarget.fieldKey || "(no field key)"}
                    </Text>
                  )}
                </div>

                <Alert
                  color={saveMode === "override" ? "violet" : "orange"}
                  icon={<IconAlertTriangle size={18} />}
                >
                  {saveMode === "override"
                    ? "This will create a product-specific page copy and attach it to this product. Shared products stay unchanged."
                    : "This will edit the shared page document. Every product using this page can inherit the change."}
                </Alert>

                {selectedDraftHasTemplateSyntax && (
                  <Alert color="blue" icon={<IconCode size={18} />}>
                    This editor shows template source text. Production PDFs render placeholders
                    and supported conditional blocks such as {"{{#if portfolioOpeningRequest.isSubPortfolio}}...{{/if}}"}.
                  </Alert>
                )}

                <Radio.Group
                  label="Save scope"
                  value={saveMode}
                  onChange={(value) => setSaveMode(value as SaveMode)}
                >
                  <Stack gap="xs" mt="xs">
                    <Radio
                      value="override"
                      label="Product-specific copy"
                      description={
                        rootAnchor
                          ? `Creates an override from ${rootAnchor.basePageId} for this product.`
                          : "Unavailable because the product attachment could not be resolved."
                      }
                      disabled={!rootAnchor}
                    />
                    {saveMode === "override" && (
                      <TextInput
                        label="Product-specific page ID"
                        value={overridePageId}
                        onChange={(event) => setOverridePageId(event.currentTarget.value)}
                        error={overridePageIdError || undefined}
                        description="Saved under config/kyc_configuration/kyc_pages and attached to this product."
                        placeholder="contract_preamble_clause_3_custody_portfolio_contract"
                        styles={{
                          input: LIST_STYLES.mono,
                        }}
                      />
                    )}
                    <Radio
                      value="shared"
                      label="Shared page"
                      description={`${pageUsageCount || 1} product(s) currently resolve this page.`}
                    />
                    {productsUsingPage.length > 0 && (
                      <Paper withBorder radius="sm" p="xs" bg="gray.0">
                        <Stack gap={6}>
                          {productsUsingPage.map((resolvedProduct) => (
                            <Group
                              key={resolvedProduct.id}
                              gap="xs"
                              justify="space-between"
                              wrap="nowrap"
                            >
                              <div style={{ minWidth: 0 }}>
                                <Group gap={6} wrap="nowrap">
                                  <Text size="sm" fw={600} lineClamp={1}>
                                    {getProductTitle(resolvedProduct)}
                                  </Text>
                                  {resolvedProduct.id === editorProduct.id && (
                                    <Badge size="xs" color="teal" variant="light">
                                      Current
                                    </Badge>
                                  )}
                                  {resolvedProduct.version && (
                                    <Badge size="xs" color="green" variant="light">
                                      v{resolvedProduct.version}
                                    </Badge>
                                  )}
                                </Group>
                                <Text size="xs" c="dimmed" style={LIST_STYLES.mono} lineClamp={1}>
                                  {resolvedProduct.id}
                                </Text>
                              </div>
                            </Group>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                </Radio.Group>

                {selectedTarget.targetType === "section" && sectionDraft && (
                  <Stack gap="sm">
                    <TextInput
                      label="Section title (English)"
                      value={sectionDraft.section_title}
                      onChange={(event) =>
                        setSectionDraft((current) => ({
                          ...(current || createSectionDraft()),
                          section_title: event.currentTarget.value,
                        }))
                      }
                    />
                    <TextInput
                      label="Section title (Arabic)"
                      value={sectionDraft.section_title_ar}
                      onChange={(event) =>
                        setSectionDraft((current) => ({
                          ...(current || createSectionDraft()),
                          section_title_ar: event.currentTarget.value,
                        }))
                      }
                    />
                    <Textarea
                      label="Section description (English)"
                      value={sectionDraft.section_description}
                      minRows={3}
                      onChange={(event) =>
                        setSectionDraft((current) => ({
                          ...(current || createSectionDraft()),
                          section_description: event.currentTarget.value,
                        }))
                      }
                    />
                    <Textarea
                      label="Section description (Arabic)"
                      value={sectionDraft.section_description_ar}
                      minRows={3}
                      onChange={(event) =>
                        setSectionDraft((current) => ({
                          ...(current || createSectionDraft()),
                          section_description_ar: event.currentTarget.value,
                        }))
                      }
                    />
                  </Stack>
                )}

                {selectedTarget.targetType === "field" && fieldDraft && (
                  <Stack gap="sm">
                    <Group gap="xs">
                      <Badge variant="light" color="blue">
                        {field?.type || selectedTarget.fieldType || "field"}
                      </Badge>
                      <Text size="sm" c="dimmed" style={LIST_STYLES.mono}>
                        index {selectedTarget.fieldIndex ?? "-"}
                      </Text>
                    </Group>
                    <TextInput
                      label="Label (English)"
                      value={fieldDraft.label || ""}
                      onChange={(event) =>
                        setFieldDraft((current) => ({
                          ...(current || createFieldDraft()),
                          label: event.currentTarget.value,
                        }))
                      }
                    />
                    <TextInput
                      label="Label (Arabic)"
                      value={fieldDraft.label_ar || ""}
                      onChange={(event) =>
                        setFieldDraft((current) => ({
                          ...(current || createFieldDraft()),
                          label_ar: event.currentTarget.value,
                        }))
                      }
                    />

                    {richTextField ? (
                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                        <TextFieldEditor
                          label="Content (English)"
                          value={fieldDraft.placeholder || ""}
                          onChange={(value) =>
                            setFieldDraft((current) => ({
                              ...(current || createFieldDraft()),
                              placeholder: value,
                            }))
                          }
                          onRemove={() =>
                            setFieldDraft((current) => ({
                              ...(current || createFieldDraft()),
                              placeholder: "",
                            }))
                          }
                          language="en"
                        />
                        <TextFieldEditor
                          label="Content (Arabic)"
                          value={fieldDraft.placeholder_ar || ""}
                          onChange={(value) =>
                            setFieldDraft((current) => ({
                              ...(current || createFieldDraft()),
                              placeholder_ar: value,
                            }))
                          }
                          onRemove={() =>
                            setFieldDraft((current) => ({
                              ...(current || createFieldDraft()),
                              placeholder_ar: "",
                            }))
                          }
                          language="ar"
                        />
                      </SimpleGrid>
                    ) : (
                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                        <Textarea
                          label="Placeholder (English)"
                          value={fieldDraft.placeholder || ""}
                          minRows={3}
                          onChange={(event) =>
                            setFieldDraft((current) => ({
                              ...(current || createFieldDraft()),
                              placeholder: event.currentTarget.value,
                            }))
                          }
                        />
                        <Textarea
                          label="Placeholder (Arabic)"
                          value={fieldDraft.placeholder_ar || ""}
                          minRows={3}
                          onChange={(event) =>
                            setFieldDraft((current) => ({
                              ...(current || createFieldDraft()),
                              placeholder_ar: event.currentTarget.value,
                            }))
                          }
                        />
                      </SimpleGrid>
                    )}

                    {valueEditableField && (
                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                        <TextInput
                          label="Value (English)"
                          value={fieldDraft.value || ""}
                          onChange={(event) =>
                            setFieldDraft((current) => ({
                              ...(current || createFieldDraft()),
                              value: event.currentTarget.value,
                            }))
                          }
                        />
                        <TextInput
                          label="Value (Arabic)"
                          value={fieldDraft.value_ar || ""}
                          onChange={(event) =>
                            setFieldDraft((current) => ({
                              ...(current || createFieldDraft()),
                              value_ar: event.currentTarget.value,
                            }))
                          }
                        />
                      </SimpleGrid>
                    )}
                  </Stack>
                )}

                <Group justify="flex-end" mt="md">
                  <Button
                    leftSection={<IconCheck size={16} />}
                    onClick={() => void handleSave()}
                    loading={isSaving}
                    disabled={saveMode === "override" && (!rootAnchor || !!overridePageIdError)}
                  >
                    Save selected block
                  </Button>
                </Group>
              </Stack>
            )}
          </ScrollArea>
        </Paper>
      </SimpleGrid>
    </Stack>
  );
};
